import React, { useRef, useState } from 'react';
import Accordion from '@mui/material/Accordion';
import AccordionSummary from '@mui/material/AccordionSummary';
import AccordionDetails from '@mui/material/AccordionDetails';
import LinearProgress from '@mui/material/LinearProgress';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import UploadFileIcon from '@mui/icons-material/UploadFile';
import VisibilityOutlinedIcon from '@mui/icons-material/VisibilityOutlined';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import FolderOpenOutlinedIcon from '@mui/icons-material/FolderOpenOutlined';
import { showToastr } from '../../ToastrMessage/Toast.helper';
import { DOCUMENT_GROUPS } from '../../../constants/documentList';
import './DocumentUploadAndVerification.styles.scss';

const getUserDocs = () => {
  try { return JSON.parse(localStorage.getItem('userDocuments') || '{}'); }
  catch { return {}; }
};

const saveUserDocs = (docs) =>
  localStorage.setItem('userDocuments', JSON.stringify(docs));

const docToId = (doc) => doc.replace(/[^a-zA-Z0-9]/g, '-').toLowerCase();

const MAX_PDF_BYTES = 1 * 1024 * 1024; // 1 MB hard cap for PDFs

// Compress an image to JPEG ≤ 1 200 px on the longest side at 75 % quality.
const compressImage = (file) =>
  new Promise((resolve) => {
    const url = URL.createObjectURL(file);
    const img = new Image();
    img.onload = () => {
      const ratio = Math.min(1200 / img.width, 1200 / img.height, 1);
      const canvas = document.createElement('canvas');
      canvas.width = Math.round(img.width * ratio);
      canvas.height = Math.round(img.height * ratio);
      canvas.getContext('2d').drawImage(img, 0, 0, canvas.width, canvas.height);
      URL.revokeObjectURL(url);
      resolve(canvas.toDataURL('image/jpeg', 0.75));
    };
    img.onerror = () => { URL.revokeObjectURL(url); resolve(null); };
    img.src = url;
  });

const TOTAL_DOCS = DOCUMENT_GROUPS.reduce((acc, g) => acc + g.docs.length, 0);

const DocumentUploadAndVerification = () => {
  const [userDocs, setUserDocs] = useState(getUserDocs);
  const [uploading, setUploading] = useState({});
  const [previewDoc, setPreviewDoc] = useState(null);
  const fileRefs = useRef({});

  const uploadedCount = Object.keys(userDocs).length;
  const overallPct = TOTAL_DOCS > 0 ? Math.round((uploadedCount / TOTAL_DOCS) * 100) : 0;

  const handleFileSelect = async (docName, file) => {
    if (!file) return;
    const isImage = ['image/jpeg', 'image/png', 'image/jpg'].includes(file.type);
    const isPdf = file.type === 'application/pdf';
    if (!isImage && !isPdf) {
      showToastr('error', 'Only PDF, JPG, or PNG files are allowed.');
      return;
    }
    if (isPdf && file.size > MAX_PDF_BYTES) {
      showToastr('error', 'PDF must be under 1 MB. Please compress it and retry.');
      return;
    }

    setUploading((p) => ({ ...p, [docName]: 0 }));

    // Simulated progress animation (runs in parallel with file processing)
    const progressPromise = new Promise((resolve) => {
      let prog = 0;
      const iv = setInterval(() => {
        prog += 20;
        setUploading((p) => ({ ...p, [docName]: Math.min(prog, 100) }));
        if (prog >= 100) { clearInterval(iv); resolve(); }
      }, 120);
    });

    const contentPromise = isImage
      ? compressImage(file)
      : new Promise((res, rej) => {
          const reader = new FileReader();
          reader.onload = (e) => res(e.target.result);
          reader.onerror = rej;
          reader.readAsDataURL(file);
        });

    const clearProgress = () =>
      setUploading((p) => { const { [docName]: _, ...rest } = p; return rest; });

    try {
      const [, content] = await Promise.all([progressPromise, contentPromise]);
      if (!content) throw new Error('Processing failed');
      const updated = {
        ...getUserDocs(),
        [docName]: { fileName: file.name, fileType: file.type, content, uploadedAt: new Date().toISOString() },
      };
      saveUserDocs(updated);
      setUserDocs(updated);
      clearProgress();
      showToastr('success', `${docName} uploaded successfully.`);
    } catch (err) {
      clearProgress();
      if (err.name === 'QuotaExceededError') {
        showToastr('error', 'Storage full — delete some documents and try again.');
      } else {
        showToastr('error', 'Upload failed. Please try again.');
      }
    }
  };

  const handleDelete = (docName) => {
    const { [docName]: _, ...rest } = userDocs;
    setUserDocs(rest);
    saveUserDocs(rest);
    showToastr('success', `${docName} removed.`);
  };

  return (
    <div className="udv">
      {/* ── Header ── */}
      <div className="udv__header">
        <div className="udv__header-left">
          <FolderOpenOutlinedIcon className="udv__header-icon" />
          <div>
            <h2 className="udv__title">My Documents</h2>
            <p className="udv__subtitle">
              Upload your documents here. They will be checked when you apply for any service.
            </p>
          </div>
        </div>
        <div className="udv__summary">
          <span className="udv__summary-count">
            <strong>{uploadedCount}</strong>&thinsp;/&thinsp;{TOTAL_DOCS}
          </span>
          <span className="udv__summary-label">uploaded</span>
        </div>
      </div>

      {/* ── Overall progress ── */}
      <div className="udv__overall-progress">
        <div className="udv__progress-row">
          <span>Overall completion</span>
          <span>{overallPct}%</span>
        </div>
        <LinearProgress
          variant="determinate"
          value={overallPct}
          className="udv__progress-bar"
        />
      </div>

      {/* ── Document groups ── */}
      <div className="udv__groups">
        {DOCUMENT_GROUPS.map(({ group, docs }) => {
          const groupUploaded = docs.filter((d) => userDocs[d]).length;
          const allDone = groupUploaded === docs.length;

          return (
            <Accordion key={group} className="udv__accordion" disableGutters elevation={0}>
              <AccordionSummary expandIcon={<ExpandMoreIcon />} className="udv__accordion-summary">
                <span className="udv__group-name">{group}</span>
                <span className={`udv__group-badge${allDone ? ' udv__group-badge--done' : ''}`}>
                  {groupUploaded}/{docs.length}
                </span>
              </AccordionSummary>

              <AccordionDetails className="udv__accordion-details">
                {docs.map((doc) => {
                  const uploaded = userDocs[doc];
                  const isUploading = uploading[doc] !== undefined;
                  const inputId = `udv-${docToId(doc)}`;

                  return (
                    <div
                      key={doc}
                      className={`udv__doc-row${uploaded ? ' udv__doc-row--uploaded' : ''}`}
                    >
                      {/* Always-present hidden input — used by both Upload label and Replace button */}
                      <input
                        type="file"
                        id={inputId}
                        accept="application/pdf,image/jpeg,image/png"
                        style={{ display: 'none' }}
                        ref={(el) => { fileRefs.current[doc] = el; }}
                        onChange={(e) => {
                          handleFileSelect(doc, e.target.files[0]);
                          e.target.value = '';
                        }}
                      />

                      <div className="udv__doc-left">
                        {uploaded
                          ? <CheckCircleIcon className="udv__doc-icon udv__doc-icon--done" />
                          : <span className="udv__doc-dot" />}
                        <span className="udv__doc-name">{doc}</span>
                      </div>

                      <div className="udv__doc-right">
                        {isUploading && (
                          <div className="udv__doc-progress">
                            <LinearProgress
                              variant="determinate"
                              value={uploading[doc]}
                              className="udv__doc-progress-bar"
                            />
                            <span className="udv__doc-progress-pct">{uploading[doc]}%</span>
                          </div>
                        )}

                        {uploaded && !isUploading && (
                          <>
                            <span className="udv__doc-filename" title={uploaded.fileName}>
                              {uploaded.fileName}
                            </span>
                            <button
                              type="button"
                              className="udv__icon-btn"
                              title="View"
                              onClick={() => setPreviewDoc({ name: doc, ...uploaded })}
                            >
                              <VisibilityOutlinedIcon fontSize="small" />
                            </button>
                            <button
                              type="button"
                              className="udv__icon-btn udv__icon-btn--replace"
                              title="Replace document"
                              onClick={() => fileRefs.current[doc]?.click()}
                            >
                              <EditOutlinedIcon fontSize="small" />
                            </button>
                            <button
                              type="button"
                              className="udv__icon-btn udv__icon-btn--delete"
                              title="Remove document"
                              onClick={() => handleDelete(doc)}
                            >
                              <DeleteOutlineIcon fontSize="small" />
                            </button>
                          </>
                        )}

                        {!uploaded && !isUploading && (
                          <label htmlFor={inputId} className="udv__upload-btn">
                            <UploadFileIcon style={{ fontSize: '0.9rem' }} />
                            Upload
                          </label>
                        )}
                      </div>
                    </div>
                  );
                })}
              </AccordionDetails>
            </Accordion>
          );
        })}
      </div>

      {/* ── Preview overlay ── */}
      {previewDoc && (
        <div className="udv__overlay" onClick={() => setPreviewDoc(null)}>
          <div className="udv__preview" onClick={(e) => e.stopPropagation()}>
            <div className="udv__preview-head">
              <span className="udv__preview-name">{previewDoc.name}</span>
              <button
                type="button"
                className="udv__preview-close"
                onClick={() => setPreviewDoc(null)}
              >
                ✕
              </button>
            </div>
            {previewDoc.fileType?.startsWith('image/') ? (
              <img
                src={previewDoc.content}
                alt={previewDoc.name}
                className="udv__preview-img"
              />
            ) : (
              <iframe
                src={previewDoc.content}
                title={previewDoc.name}
                className="udv__preview-iframe"
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default DocumentUploadAndVerification;
